// Lecturas del equipo servidas desde el VPS (scripts/vpsCacheServer.js).
//
// Una pestaña descarga la colección entera una vez y recibe después los
// documentos que cambian. Firestore solo cobra el listener del VPS, no uno por
// pestaña. Las escrituras NO pasan por aquí: siguen yendo a Firestore.
//
// Si el VPS no responde, cada pantalla vuelve a su listener de Firestore de
// siempre (`alternativa`), así que un fallo del VPS cuesta dinero, no datos.
// Con REACT_APP_CACHE_URL vacío no se usa nunca.
import { onAuthStateChanged } from "firebase/auth";
import { FieldValue, GeoPoint, Timestamp, deleteField, doc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "./firebase";
import { versionPropia, versionPublicada } from "./nuevaVersion";

export type Fila = { id: string; [campo: string]: any };
type Coleccion = "crmLeads" | "whatsappThreads";
// `fallo` abre Firestore; la suscripción sigue apuntada y, cuando el VPS
// vuelve a servir, `cambio` pinta sus datos y cierra Firestore.
type Suscriptor = { cambio: () => void; fallo: () => void };

// El build sustituye `process.env` por un objeto (DefinePlugin de
// webpack.andi.config.js). En el navegador `process` no existe: preguntar por
// `typeof process` dejaba la URL vacía y el espejo apagado sin avisar.
// El panel de administración se construye con react-scripts, que copia la
// variable tal cual: `off` también lo apaga ahí.
const URL_CRUDA = String(process.env.REACT_APP_CACHE_URL || "").trim();
const URL_BASE = /^off$/i.test(URL_CRUDA) ? "" : URL_CRUDA.replace(/\/+$/, "");
// Incluye el arranque del VPS: tras un reinicio tarda ~10 s en cargar crmLeads.
const ESPERA_DESCARGA_MS = 25000;
// Una recarga con datos ya en pantalla puede esperar más antes de rendirse.
const ESPERA_RECARGA_MS = 90000;
// Si el flujo no vuelve en este tiempo, contado desde que se cortó, los datos
// pueden estar viejos: se abandona. Solo cuenta con la pestaña a la vista y con
// red: Chrome retrasa hasta un minuto los temporizadores de una pestaña oculta,
// y con 60 s un portátil que despertaba abandonaba al segundo intento (17/09,
// ráfagas de 20.000 lecturas a las 09:34 y a la vuelta de comer).
const SIN_FLUJO_MAX_MS = 180000;
// El VPS manda un comentario cada 25 s. Un flujo callado más que esto está
// muerto aunque no dé error (conexión a medias tras suspender): se reabre.
const FLUJO_MUDO_MS = 60000;
const VIGILANCIA_FLUJO_MS = 15000;
// Al volver a la pestaña, un flujo sin señal desde hace más de esto se reabre
// ya, sin esperar al vigilante.
const FLUJO_DUDOSO_MS = 35000;
// Tras pasar a Firestore se vuelve a probar el VPS: la prueba es una descarga
// del VPS (gratis) y el listener de Firestore se cierra cuando responde. Si
// vuelve a fallar enseguida, se espera el doble, hasta 10 min: cada ida y
// vuelta cuesta una lectura entera en Firestore.
const VOLVER_CADA_MS = 120000;
const VOLVER_MAX_MS = 600000;
const VOLVER_ESTABLE_MS = 600000;
const VIGILANCIA_VUELTA_MS = 30000;

const oculta = () => typeof document !== "undefined" && document.visibilityState === "hidden";
const sinRed = () => typeof navigator !== "undefined" && navigator.onLine === false;

export const espejoConfigurado = () => URL_BASE !== "";

export function decodificar(valor: any): any {
  if (valor === null || typeof valor !== "object") return valor;
  if (Array.isArray(valor)) return valor.map(decodificar);
  const claves = Object.keys(valor);
  if (claves.length === 1) {
    if (typeof valor.__ts === "number") return Timestamp.fromMillis(valor.__ts);
    if (Array.isArray(valor.__geo)) return new GeoPoint(valor.__geo[0], valor.__geo[1]);
    if (typeof valor.__ref === "string") return doc(db, valor.__ref);
  }
  const salida: any = {};
  for (const clave of claves) salida[clave] = decodificar(valor[clave]);
  return salida;
}

// Igual que `{ id: d.id, ...d.data() }`: si el documento trae un campo `id`,
// gana ese, como en el resto de ANDI.
const aFila = (id: string, datos: any): Fila => ({ id, ...decodificar(datos) });

class Espejo {
  filas = new Map<string, Fila>();
  seq = 0;
  epoch = "";
  // Secuencia que anunció el servidor al abrir el flujo (ver `recibir`).
  anunciada = 0;
  estado: "parado" | "cargando" | "listo" | "fallido" = "parado";
  pendientes: any[] = [];
  suscriptores = new Set<Suscriptor>();
  version = 0;
  private lista: Fila[] | null = null;
  constructor(public nombre: Coleccion) {}

  todas(): Fila[] {
    if (!this.lista) this.lista = Array.from(this.filas.values());
    return this.lista;
  }

  aplicar(cambios: [string, any][]) {
    for (const [id, datos] of cambios) {
      if (datos === null) this.filas.delete(id);
      else this.filas.set(id, aFila(id, datos));
    }
    this.tocado();
  }

  tocado() {
    this.lista = null;
    this.version++;
    Array.from(this.suscriptores).forEach((s) => s.cambio());
  }
}

const espejos: Record<Coleccion, Espejo> = {
  crmLeads: new Espejo("crmLeads"),
  whatsappThreads: new Espejo("whatsappThreads"),
};
// Colecciones que no se replican (el historial): quién quiere saber que
// cambiaron y qué hacer si el VPS se abandona.
type AvisoCambio = { aviso: () => void; alFallar: () => void };
const avisosCambio = new Map<string, Set<AvisoCambio>>();
// Última secuencia vista de cada una, para avisar si al reconectar hay cambios
// que no llegaron.
const seqAvisos = new Map<string, number>();

async function token(): Promise<string> {
  const usuario = auth.currentUser;
  if (!usuario) throw new Error("sin sesión");
  return usuario.getIdToken();
}

// Con la versión de la página: el VPS apunta quién sigue con una vieja.
async function cabeceras(): Promise<Record<string, string>> {
  const h: Record<string, string> = { Authorization: `Bearer ${await token()}` };
  const propia = versionPropia();
  const publicada = versionPublicada();
  if (propia) h["X-Version"] = propia;
  if (publicada) h["X-Version-Publicada"] = publicada;
  return h;
}

async function pedir(ruta: string, señal?: AbortSignal): Promise<Response> {
  const res = await fetch(URL_BASE + ruta, {
    headers: await cabeceras(),
    signal: señal,
    cache: "no-store",
  });
  if (!res.ok) throw Object.assign(new Error(`vps ${res.status}`), { status: res.status });
  return res;
}

// Tras un reinicio el VPS contesta 503 mientras carga la colección, y 429 si
// muchas pestañas del mismo usuario descargan a la vez; al despertar el equipo
// la red tarda unos segundos (fetch falla sin código). Se reintenta dentro del
// plazo de la descarga en vez de pasar a Firestore. Un 401/403/500 no.
async function pedirTrasArranque(ruta: string, señal: AbortSignal): Promise<Response> {
  for (let espera = 1000; ; espera = Math.min(espera + 1000, 4000)) {
    try {
      return await pedir(ruta, señal);
    } catch (e: any) {
      const pasajero = e?.status === 503 || e?.status === 429 || e?.status === undefined;
      if (!pasajero || señal.aborted) throw e;
    }
    // Un poco de azar para que las pestañas no reintenten todas a la vez.
    await new Promise((listo) => setTimeout(listo, espera + Math.random() * 500));
    if (señal.aborted) throw new Error("vps sin arrancar");
  }
}

// ── Flujo de cambios (SSE sobre fetch: EventSource no admite cabeceras) ──
let flujo: AbortController | null = null;
let columnasFlujo = "";
// Última señal de vida del flujo (dato, comentario o apertura).
let ultimaSeñal = 0;
// Desde cuándo no hay flujo sano (0: lo hay).
let sinFlujoDesde = 0;
let vigilancia: ReturnType<typeof setInterval> | null = null;
let reintento: ReturnType<typeof setTimeout> | null = null;
let esperaReintento = 1000;
let epochFlujo = "";
let conSince = new Set<string>();
let ultimaEpoch = "";

function columnasActivas(): string[] {
  const cols = (Object.values(espejos) as Espejo[])
    .filter((e) => e.estado === "cargando" || e.estado === "listo").map((e) => e.nombre as string);
  Array.from(avisosCambio.entries()).forEach(([col, avisos]) => { if (avisos.size) cols.push(col); });
  // Los mensajes entran en el flujo cuando hay una posición desde la que
  // pedirlos (la da la primera descarga de un chat).
  if (mensajes.epoch && mensajes.chats.size) cols.push(COL_MENSAJES);
  return cols.sort();
}

function reiniciarFlujo() {
  const cols = columnasActivas();
  if (cols.join(",") === columnasFlujo && flujo) return;
  flujo?.abort();
  flujo = null;
  columnasFlujo = cols.join(",");
  if (reintento) { clearTimeout(reintento); reintento = null; }
  if (cols.length) void abrirFlujo(cols);
}

function programarReintento() {
  if (reintento || !columnasFlujo) return;
  // Oculta o sin red nadie está mirando, y al volver se pide desde la posición
  // que se tenía: no hay por qué pasar a Firestore.
  if (oculta() || sinRed()) sinFlujoDesde = 0;
  else if (!sinFlujoDesde) sinFlujoDesde = Date.now();
  else if (Date.now() - sinFlujoDesde > SIN_FLUJO_MAX_MS) {
    abandonar();
    return;
  }
  reintento = setTimeout(() => {
    reintento = null;
    flujo = null;
    const cols = columnasActivas();
    columnasFlujo = cols.join(",");
    if (cols.length) void abrirFlujo(cols);
  }, esperaReintento);
  esperaReintento = Math.min(esperaReintento * 2, 15000);
}

function pararVigilancia() {
  if (vigilancia) clearInterval(vigilancia);
  vigilancia = null;
}

// Los temporizadores siguen al despertar el equipo, el flujo muerto no avisa.
function vigilarFlujo() {
  if (vigilancia) return;
  vigilancia = setInterval(() => {
    if (!columnasFlujo) { pararVigilancia(); return; }
    if (!flujo || Date.now() - ultimaSeñal <= FLUJO_MUDO_MS) return;
    const muerto = flujo;
    flujo = null;
    muerto.abort();
    programarReintento();
  }, VIGILANCIA_FLUJO_MS);
}

async function abrirFlujo(cols: string[]) {
  const control = new AbortController();
  flujo = control;
  ultimaSeñal = Date.now();
  vigilarFlujo();
  const posiciones: Array<[string, number, string]> = [];
  cols.forEach((c) => {
    if (c in espejos && espejos[c as Coleccion].estado === "listo") {
      const e = espejos[c as Coleccion];
      posiciones.push([c, e.seq, e.epoch]);
    }
    if (c === COL_MENSAJES) posiciones.push([c, mensajes.seq, mensajes.epoch]);
  });
  // Solo posiciones del mismo proceso del VPS (el de la última descarga); las
  // de otro van sin `since` y el saludo decide qué hay que descargar otra vez.
  const epoch = posiciones.some((p) => p[2] === ultimaEpoch) ? ultimaEpoch : posiciones[0]?.[2] || "";
  const since = posiciones.filter((p) => p[2] === epoch).map(([c, seq]) => `${c}:${seq}`).join(",");
  const params = new URLSearchParams({ cols: cols.join(",") });
  conSince = new Set();
  if (since && epoch) {
    params.set("since", since);
    params.set("epoch", epoch);
    since.split(",").forEach((parte) => conSince.add(parte.split(":")[0]));
  }
  try {
    const res = await pedir(`/api/events?${params}`, control.signal);
    const lector = res.body!.getReader();
    const texto = new TextDecoder();
    let resto = "";
    for (;;) {
      const { value, done } = await lector.read();
      // Un trozo ya leído de un flujo cerrado no se aplica: puede ser de otra
      // posición (o de otro proceso del VPS).
      if (done || flujo !== control) break;
      resto += texto.decode(value, { stream: true });
      let corte;
      while ((corte = resto.indexOf("\n\n")) >= 0) {
        const bloque = resto.slice(0, corte);
        resto = resto.slice(corte + 2);
        for (const linea of bloque.split("\n")) {
          if (!linea.startsWith("data: ")) continue;
          ultimaSeñal = Date.now();
          sinFlujoDesde = 0;
          esperaReintento = 1000;
          recibir(JSON.parse(linea.slice(6)));
        }
        if (bloque.startsWith(":")) ultimaSeñal = Date.now();
      }
    }
  } catch {
    if (control.signal.aborted) return;
  }
  if (flujo === control) programarReintento();
}

function recibir(msg: any) {
  if (msg.type === "hello") {
    const otraEpoch = epochFlujo !== "" && epochFlujo !== msg.epoch;
    epochFlujo = msg.epoch;
    saludoMensajes(msg);
    Array.from(avisosCambio.entries()).forEach(([col, avisos]) => {
      const anunciada = msg.seq?.[col];
      if (typeof anunciada !== "number") return;
      const vista = seqAvisos.get(col);
      seqAvisos.set(col, anunciada);
      if ((vista !== undefined && vista !== anunciada) || otraEpoch) Array.from(avisos).forEach((a) => a.aviso());
    });
    (Object.values(espejos) as Espejo[]).forEach((e) => {
      const anunciada = msg.seq?.[e.nombre];
      if (typeof anunciada !== "number") return;
      e.anunciada = anunciada;
      if (e.estado !== "listo") return;
      // Otro proceso (el VPS se reinició): sus secuencias no se comparan.
      if (e.epoch !== msg.epoch) { void descargar(e); return; }
      // Sin `since` el servidor no reenvía lo perdido entre la descarga y
      // este flujo; con `since`, lo que falte llega justo detrás.
      if (!conSince.has(e.nombre) && anunciada > e.seq) void descargar(e);
    });
    return;
  }
  if (msg.type === "changed") {
    if (typeof msg.seq === "number") seqAvisos.set(msg.col, msg.seq);
    Array.from(avisosCambio.get(msg.col) || []).forEach((a) => a.aviso());
    return;
  }
  if (msg.col === COL_MENSAJES) { recibirMensajes(msg); return; }
  const espejo = espejos[msg.col as Coleccion];
  if (!espejo) return;
  if (msg.type === "resync") { void descargar(espejo); return; }
  if (msg.type !== "docs") return;
  if (espejo.estado === "cargando") { espejo.pendientes.push(msg); return; }
  if (espejo.estado !== "listo" || msg.seq <= espejo.seq) return;
  if (msg.seq !== espejo.seq + 1) { void descargar(espejo); return; }
  espejo.seq = msg.seq;
  espejo.aplicar(msg.changes);
}

async function descargar(espejo: Espejo) {
  if (espejo.estado === "fallido") return;
  const conDatos = espejo.estado === "listo";
  espejo.estado = "cargando";
  espejo.pendientes = [];
  reiniciarFlujo();
  const control = new AbortController();
  const limite = setTimeout(() => control.abort(), conDatos ? ESPERA_RECARGA_MS : ESPERA_DESCARGA_MS);
  try {
    const cuerpo = await (await pedirTrasArranque(`/api/mirror/${espejo.nombre}`, control.signal)).json();
    const filas = new Map<string, Fila>();
    for (const [id, datos] of cuerpo.docs) filas.set(id, aFila(id, datos));
    espejo.filas = filas;
    espejo.seq = cuerpo.seq;
    espejo.epoch = cuerpo.epoch;
    ultimaEpoch = cuerpo.epoch;
    espejo.estado = "listo";
    vueltaConfirmada();
    const pendientes = espejo.pendientes.sort((a, b) => a.seq - b.seq);
    espejo.pendientes = [];
    let hueco = false;
    for (const msg of pendientes) {
      if (msg.seq <= espejo.seq) continue;
      if (msg.seq !== espejo.seq + 1) { hueco = true; break; }
      espejo.seq = msg.seq;
      for (const [id, datos] of msg.changes) {
        if (datos === null) espejo.filas.delete(id);
        else espejo.filas.set(id, aFila(id, datos));
      }
    }
    espejo.tocado();
    if (epochFlujo && epochFlujo !== espejo.epoch) {
      // El flujo abierto es de otro proceso del VPS: se abre uno nuevo.
      flujo?.abort();
      flujo = null;
      columnasFlujo = "";
    }
    reiniciarFlujo();
    // Cambios anunciados por el flujo que no estaban en la descarga ni han
    // llegado detrás: faltan, se vuelve a descargar.
    if (hueco || (epochFlujo === espejo.epoch && espejo.anunciada > espejo.seq)) void descargar(espejo);
  } catch {
    // Una recarga fallida con datos ya servidos también se abandona: sin ella
    // no sabemos qué cambios faltan.
    fallar(espejo);
    anotarFallo();
  } finally {
    clearTimeout(limite);
  }
}

function fallar(espejo: Espejo) {
  espejo.estado = "fallido";
  espejo.filas = new Map();
  espejo.pendientes = [];
  // Las suscripciones siguen apuntadas (ya en Firestore) para poder volver.
  Array.from(espejo.suscriptores).forEach((s) => s.fallo());
  reiniciarFlujo();
  vigilarVuelta();
}

function abandonar() {
  (Object.values(espejos) as Espejo[]).forEach((e) => { if (e.estado !== "parado") fallar(e); });
  if (mensajes.chats.size) fallarMensajes();
  // El historial se entera y pasa a su listener; si no, se quedaba quieto con
  // lo último que le dio el VPS.
  const avisos: AvisoCambio[] = [];
  avisosCambio.forEach((conjunto) => conjunto.forEach((a) => avisos.push(a)));
  avisosCambio.clear();
  seqAvisos.clear();
  flujo?.abort();
  flujo = null;
  columnasFlujo = "";
  pararVigilancia();
  anotarFallo();
  avisos.forEach((a) => a.alFallar());
}

// ── Vuelta al VPS tras pasar a Firestore ───────────────────────────────────
// Antes una pestaña que pasaba a Firestore se quedaba ahí hasta recargarla, y
// el 17/09 un tercio de las pestañas abiertas seguía leyendo de Firestore.
const avisosVuelta = new Set<() => void>();
let vigilanciaVuelta: ReturnType<typeof setInterval> | null = null;
let proximaVuelta = 0;
let esperaVuelta = VOLVER_CADA_MS;
let ultimaVueltaOk = 0;

function hayQueVolver() {
  return (Object.values(espejos) as Espejo[]).some((e) => e.estado === "fallido" && e.suscriptores.size > 0)
    || (mensajes.fallido && chatsEnFirestore.size > 0) || avisosVuelta.size > 0;
}

function anotarFallo() {
  // Si acababa de volver y ha caído otra vez, se espera más la próxima vez.
  esperaVuelta = ultimaVueltaOk && Date.now() - ultimaVueltaOk < VOLVER_ESTABLE_MS
    ? Math.min(esperaVuelta * 2, VOLVER_MAX_MS) : VOLVER_CADA_MS;
  ultimaVueltaOk = 0;
  proximaVuelta = Date.now() + esperaVuelta;
  vigilarVuelta();
}

function vueltaConfirmada() {
  if (!ultimaVueltaOk) ultimaVueltaOk = Date.now();
}

function vigilarVuelta() {
  if (vigilanciaVuelta) return;
  if (!proximaVuelta) proximaVuelta = Date.now() + esperaVuelta;
  vigilanciaVuelta = setInterval(intentarVolver, VIGILANCIA_VUELTA_MS);
}

function pararVuelta() {
  if (vigilanciaVuelta) clearInterval(vigilanciaVuelta);
  vigilanciaVuelta = null;
}

function intentarVolver() {
  if (!hayQueVolver()) { pararVuelta(); return; }
  if (!espejoConfigurado() || !auth.currentUser || oculta() || sinRed()) return;
  if (Date.now() < proximaVuelta) return;
  proximaVuelta = Date.now() + esperaVuelta;
  (Object.values(espejos) as Espejo[]).forEach((e) => {
    if (e.estado !== "fallido" || !e.suscriptores.size) return;
    e.estado = "parado";
    void descargar(e);
  });
  if (mensajes.fallido && chatsEnFirestore.size) volverMensajes();
  Array.from(avisosVuelta).forEach((aviso) => aviso());
}

/**
 * Para quien pasó a Firestore por su cuenta (el historial): se le llama cuando
 * toca probar otra vez el VPS. Devuelve cómo darse de baja.
 */
export function alVolverEspejo(aviso: () => void): () => void {
  if (!espejoConfigurado()) return () => {};
  avisosVuelta.add(aviso);
  vigilarVuelta();
  return () => { avisosVuelta.delete(aviso); };
}

/**
 * Escucha una consulta sobre el espejo. `seleccionar` recibe todas las filas y
 * devuelve las que tocan (filtro + orden + límite, lo mismo que la consulta de
 * Firestore). Si el espejo no se puede usar, o falla después, se llama a
 * `alternativa`, que abre el listener de Firestore y devuelve su baja.
 */
export function escucharEspejo(
  coleccion: Coleccion,
  seleccionar: (filas: Fila[]) => Fila[],
  alSiguiente: (filas: Fila[]) => void,
  alternativa: () => () => void,
): () => void {
  const espejo = espejos[coleccion];
  if (!espejoConfigurado() || !auth.currentUser) return alternativa();
  let activo = true;
  let bajaAlternativa: (() => void) | null = null;
  let versionVista = -1;
  let anterior: Fila[] | null = null;
  const sub: Suscriptor = {
    cambio: () => {
      if (!activo || espejo.estado !== "listo" || espejo.version === versionVista) return;
      versionVista = espejo.version;
      const filas = seleccionar(espejo.todas());
      if (bajaAlternativa) {
        // El VPS ha vuelto: primero sus datos, luego se cierra Firestore.
        const baja = bajaAlternativa;
        bajaAlternativa = null;
        anterior = filas;
        alSiguiente(filas);
        baja();
        return;
      }
      // Como Firestore: solo se avisa si el resultado cambia. Cada escritura
      // en crmLeads llega a todas las consultas, y repintar una tabla de mil
      // filas por un lead de otra etapa frena la pantalla.
      if (anterior && anterior.length === filas.length && filas.every((f, i) => f === anterior![i])) return;
      anterior = filas;
      alSiguiente(filas);
    },
    fallo: () => {
      if (!activo || bajaAlternativa) return;
      bajaAlternativa = alternativa();
    },
  };
  espejo.suscriptores.add(sub);
  if (espejo.estado === "fallido") { sub.fallo(); vigilarVuelta(); }
  else if (espejo.estado === "parado") void descargar(espejo);
  else if (espejo.estado === "listo") queueMicrotask(sub.cambio);
  return () => {
    activo = false;
    espejo.suscriptores.delete(sub);
    bajaAlternativa?.();
  };
}

/** Foto puntual (lo que hacía un `getDocs`). Rechaza si el espejo no sirve. */
export function leerEspejo(coleccion: Coleccion, seleccionar: (filas: Fila[]) => Fila[]): Promise<Fila[]> {
  const espejo = espejos[coleccion];
  // Un panel que se abre lee la colección entera: antes de hacerlo en
  // Firestore se prueba el VPS otra vez (sin esperar a la vigilancia).
  if (espejo.estado === "fallido" && espejoConfigurado() && auth.currentUser && !oculta()) espejo.estado = "parado";
  return new Promise((resolve, reject) => {
    let baja: () => void = () => {};
    let hecho = false;
    baja = escucharEspejo(coleccion, seleccionar, (filas) => {
      if (hecho) return;
      hecho = true;
      queueMicrotask(() => baja());
      resolve(filas);
    }, () => {
      if (!hecho) {
        hecho = true;
        queueMicrotask(() => baja());
        reject(new Error("espejo no disponible"));
      }
      return () => {};
    });
  });
}

// Lo que Firestore enseñaría al momento de una escritura propia.
function aplicarCampos(fila: Fila, cambios: Record<string, any>): Fila {
  for (const [campo, valor] of Object.entries(cambios)) {
    if (campo.includes(".")) continue;
    if (valor instanceof FieldValue) {
      if (valor.isEqual(serverTimestamp())) fila[campo] = Timestamp.now();
      else if (valor.isEqual(deleteField())) delete fila[campo];
      // increment, arrayUnion…: se espera a lo que diga el servidor.
      continue;
    }
    fila[campo] = valor;
  }
  return fila;
}

/**
 * Cambio hecho desde esta pestaña: se ve ya, sin esperar a que vuelva por el
 * VPS (con el listener de Firestore también se veía al instante). Lo que diga
 * el servidor después lo sustituye.
 */
export function parcheLocal(coleccion: Coleccion, id: string, cambios: Record<string, any> | null): () => void {
  const espejo = espejos[coleccion];
  const nada = () => {};
  if (espejo.estado !== "listo") return nada;
  const actual = espejo.filas.get(id);
  if (!actual) return nada;
  let nueva: Fila | undefined;
  if (cambios === null) {
    espejo.filas.delete(id);
  } else {
    nueva = aplicarCampos({ ...actual }, cambios);
    espejo.filas.set(id, nueva);
  }
  espejo.tocado();
  // Si Firestore rechaza la escritura, se vuelve a lo de antes (el listener
  // de Firestore también la retiraba). Solo si nadie la ha tocado después.
  return () => {
    if (espejo.estado !== "listo" || espejo.filas.get(id) !== nueva) return;
    espejo.filas.set(id, actual);
    espejo.tocado();
  };
}

/** Historial (crmLeadEvents): consulta al VPS, que lo tiene entero en memoria. */
export async function consultarHistorial(params: { field?: string; leadId?: string; by?: string; limit?: number }): Promise<Fila[]> {
  if (!espejoConfigurado() || !auth.currentUser) throw new Error("espejo no configurado");
  const q = new URLSearchParams();
  if (params.field) q.set("field", params.field);
  if (params.leadId) q.set("leadId", params.leadId);
  if (params.by) q.set("by", params.by);
  if (params.limit) q.set("limit", String(params.limit));
  const control = new AbortController();
  const limite = setTimeout(() => control.abort(), ESPERA_DESCARGA_MS);
  try {
    const cuerpo = await (await pedirTrasArranque(`/api/crmLeadEvents?${q}`, control.signal)).json();
    return cuerpo.docs.map(([id, datos]: [string, any]) => aFila(id, datos));
  } finally {
    clearTimeout(limite);
  }
}

// ── Casos Cofidis del panel de administración ──────────────────────────────
// No se copian a la pestaña: cada página, búsqueda o total se pregunta al VPS,
// que contesta lo mismo que la consulta de Firestore del panel. Si no
// contesta, quien llama hace su consulta de Firestore; durante 2 min después
// de un fallo ni se prueba, para no esperar el plazo en cada clic.
const ESPERA_CASOS_MS = 10000;
const PAUSA_CASOS_MS = 120000;
let casosSinVpsHasta = 0;

export type CursorCaso = { fecha: string; id: string };

async function pedirCasos(ruta: string, espera: number): Promise<any> {
  if (!espejoConfigurado() || !auth.currentUser) throw new Error("espejo no configurado");
  if (Date.now() < casosSinVpsHasta) throw new Error("espejo en pausa");
  const control = new AbortController();
  const limite = setTimeout(() => control.abort(), espera);
  try {
    return await (await pedirTrasArranque(ruta, control.signal)).json();
  } catch (e) {
    casosSinVpsHasta = Date.now() + PAUSA_CASOS_MS;
    throw e;
  } finally {
    clearTimeout(limite);
  }
}

function filtrosCasos(p: { operacionesAgent?: string; comercialAgent?: string }): URLSearchParams {
  const q = new URLSearchParams();
  if (p.operacionesAgent) q.set("operacionesAgent", p.operacionesAgent);
  if (p.comercialAgent) q.set("comercialAgent", p.comercialAgent);
  return q;
}

/**
 * Una ventana de la lista: `orderBy('registeredDate', 'desc')`, filtros de
 * agente, `searchPrefixes` si hay tokens, `startAfter(despues)` y `limit`.
 * Devuelve el id del documento aparte (la fila puede traer su propio `id`).
 */
export async function consultarCasos(p: {
  operacionesAgent?: string; comercialAgent?: string; tokens?: string[]; limite: number; despues?: CursorCaso | null;
}): Promise<Array<{ id: string; fila: Fila }>> {
  const q = filtrosCasos(p);
  (p.tokens || []).forEach((token) => q.append("token", token));
  q.set("limit", String(p.limite));
  if (p.despues) {
    q.set("despuesFecha", p.despues.fecha);
    q.set("despuesId", p.despues.id);
  }
  const cuerpo = await pedirCasos(`/api/cofidisCases?${q}`, ESPERA_CASOS_MS);
  return cuerpo.docs.map(([id, datos]: [string, any]) => ({ id, fila: aFila(id, datos) }));
}

/** Lo que daba `getCountFromServer` con los filtros de agente. */
export async function contarCasos(p: { operacionesAgent?: string; comercialAgent?: string }): Promise<number> {
  const q = filtrosCasos(p);
  q.set("contar", "1");
  const cuerpo = await pedirCasos(`/api/cofidisCases?${q}`, ESPERA_CASOS_MS);
  if (typeof cuerpo.n !== "number") throw new Error("respuesta sin total");
  return cuerpo.n;
}

/** Todos los casos, en el orden de la colección (por id), para el CSV. */
export async function descargarCasos(): Promise<Array<{ id: string; datos: any }>> {
  const cuerpo = await pedirCasos("/api/mirror/cofidisCases", ESPERA_DESCARGA_MS);
  return cuerpo.docs.map(([id, datos]: [string, any]) => ({ id, datos: decodificar(datos) }));
}

// Usuarios: con los grupos de WhatsApp decide qué se enseña, así que el plazo
// es corto; si el VPS no contesta, quien llama los lee de Firestore.
const ESPERA_USUARIOS_MS = 8000;
let usuariosEnCurso: { uid: string; promesa: Promise<Fila[]> } | null = null;

export type EstadoVersion = "antigua" | "pendiente" | "al_dia" | "sin_dato";
export type FilaVersion = {
  uid: string; nombre: string; email: string | null; app: string; version: string | null;
  primera: number; ultima: number; abiertos: number; estado: EstadoVersion;
};
export type InformeVersiones = { ahora: number; actuales: Record<string, string>; filas: FilaVersion[] };

/** Qué versión de ANDI y WhatsApp tiene abierta cada persona (solo administradores). */
export async function leerVersiones(): Promise<InformeVersiones> {
  if (!espejoConfigurado() || !auth.currentUser) throw new Error("espejo no configurado");
  return (await pedir("/api/versiones")).json();
}

/**
 * Los usuarios que las reglas dejan leer: el propio y, con el rol admin del
 * token, todos. ANDI y WhatsApp los leían de Firestore en cada carga. Rechaza
 * si el VPS no sirve.
 */
export function leerUsuarios(): Promise<Fila[]> {
  const usuario = auth.currentUser;
  if (!espejoConfigurado() || !usuario) return Promise.reject(new Error("espejo no configurado"));
  // Las dos pantallas que lo piden al cargar comparten la misma petición.
  if (usuariosEnCurso && usuariosEnCurso.uid === usuario.uid) return usuariosEnCurso.promesa;
  const control = new AbortController();
  const limite = setTimeout(() => control.abort(), ESPERA_USUARIOS_MS);
  const promesa = pedirTrasArranque("/api/users", control.signal)
    .then((res) => res.json())
    .then((cuerpo) => cuerpo.docs.map(([id, datos]: [string, any]) => aFila(id, datos)) as Fila[])
    .finally(() => {
      clearTimeout(limite);
      setTimeout(() => { if (usuariosEnCurso?.promesa === promesa) usuariosEnCurso = null; }, 1000);
    });
  usuariosEnCurso = { uid: usuario.uid, promesa };
  return promesa;
}

/**
 * Aviso cuando cambia una colección que no se replica (el historial).
 * `alFallar` se llama si se abandona el VPS: quien escucha tiene que pasar a
 * Firestore, porque ya no le llegarán avisos.
 */
export function alCambiar(coleccion: "crmLeadEvents", aviso: () => void, alFallar: () => void = () => {}): () => void {
  if (!espejoConfigurado()) return () => {};
  let avisos = avisosCambio.get(coleccion);
  if (!avisos) { avisos = new Set(); avisosCambio.set(coleccion, avisos); }
  const entrada = { aviso, alFallar };
  avisos.add(entrada);
  const propios = avisos;
  reiniciarFlujo();
  return () => {
    propios.delete(entrada);
    if (!propios.size && avisosCambio.get(coleccion) === propios) {
      avisosCambio.delete(coleccion);
      seqAvisos.delete(coleccion);
    }
    reiniciarFlujo();
  };
}

// ── Mensajes de WhatsApp ─────────────────────────────────────────────────
// El VPS escucha todos los mensajes; una pestaña solo guarda los de sus chats
// abiertos (y un minuto los de los que acaba de cerrar, para volver sin
// esperar). El flujo trae los cambios de todos los chats, numerados en una
// sola secuencia: cada chat aplica los suyos posteriores a su descarga.
const COL_MENSAJES = "whatsappMessages";
const MENSAJES_RECORDADOS_MS = 60000;
type SubMensajes = { emitir: (filas: Fila[]) => void; fallo: () => void };
// Chats cuyas suscripciones están en Firestore, para volver cuando el VPS sirva.
const chatsEnFirestore = new Map<string, Set<SubMensajes>>();
type Chat = {
  id: string;
  filas: Map<string, Fila>;
  base: number;                              // secuencia de su descarga
  cargando: boolean;
  inicio: number | null;                     // posición del flujo al pedirla
  pendientes: Array<[number, number, string, any]>; // generación, seq, id, datos
  subs: Set<SubMensajes>;
  control: AbortController | null;
  olvido: ReturnType<typeof setTimeout> | null;
};
const mensajes = {
  fallido: false,
  epoch: "",       // vacío: todavía no hay posición en el flujo
  seq: 0,          // último cambio de mensajes visto (o incluido en descargas)
  anunciada: 0,
  generacion: 0,   // cambia al reabrir el flujo desde una posición nueva
  recargadosConSaludo: false,
  chats: new Map<string, Chat>(),
};

function avisarChat(chat: Chat) {
  const filas = Array.from(chat.filas.values());
  Array.from(chat.subs).forEach((s) => s.emitir(filas));
}

function aplicarAChat(chat: Chat, id: string, datos: any) {
  if (datos === null) chat.filas.delete(id);
  else chat.filas.set(id, aFila(id, datos));
}

function recargarChats(excepto?: Chat) {
  Array.from(mensajes.chats.values()).forEach((c) => { if (c !== excepto) void descargarChat(c); });
}

async function descargarChat(chat: Chat) {
  if (chat.control) { const anterior = chat.control; chat.control = null; anterior.abort(); }
  const control = new AbortController();
  chat.control = control;
  chat.cargando = true;
  chat.pendientes = [];
  chat.inicio = mensajes.epoch ? mensajes.seq : null;
  const limite = setTimeout(() => control.abort(), ESPERA_DESCARGA_MS);
  try {
    const res = await pedirTrasArranque(`/api/whatsappThreads/${encodeURIComponent(chat.id)}/messages`, control.signal);
    const cuerpo = await res.json();
    // Sustituida por otra descarga, o el chat ya no está.
    if (chat.control !== control) return;
    ultimaEpoch = cuerpo.epoch;
    let nuevaPosicion = false;
    if (!mensajes.epoch || cuerpo.epoch !== mensajes.epoch) {
      // Primera posición, o el VPS se reinició: esta descarga marca desde
      // dónde se piden los cambios. El flujo se reabre con ese `since` y
      // repite todo lo posterior, así que lo acumulado antes no se usa.
      const otroProceso = !!mensajes.epoch;
      mensajes.epoch = cuerpo.epoch;
      mensajes.seq = cuerpo.seq;
      mensajes.anunciada = cuerpo.seq;
      mensajes.generacion++;
      nuevaPosicion = true;
      if (otroProceso) recargarChats(chat);
    } else if (chat.inicio === null && cuerpo.seq < mensajes.seq) {
      // Se pidió sin posición y el flujo ya iba por delante: puede faltar algo.
      void descargarChat(chat);
      return;
    }
    const filas = new Map<string, Fila>();
    for (const [id, datos] of cuerpo.docs) filas.set(id, aFila(id, datos));
    chat.filas = filas;
    chat.base = cuerpo.seq;
    chat.cargando = false;
    chat.control = null;
    for (const [generacion, seq, id, datos] of chat.pendientes) {
      if (generacion === mensajes.generacion && seq > chat.base) aplicarAChat(chat, id, datos);
    }
    chat.pendientes = [];
    vueltaConfirmada();
    avisarChat(chat);
    if (nuevaPosicion) {
      flujo?.abort();
      flujo = null;
      columnasFlujo = "";
      reiniciarFlujo();
    }
  } catch {
    if (chat.control === control) { fallarMensajes(); anotarFallo(); }
  } finally {
    clearTimeout(limite);
  }
}

function saludoMensajes(msg: any) {
  mensajes.recargadosConSaludo = false;
  const anunciada = msg.seq?.[COL_MENSAJES];
  if (typeof anunciada !== "number" || !mensajes.epoch) return;
  mensajes.anunciada = anunciada;
  // Otro proceso, o un flujo sin `since` que empieza más adelante: lo de en
  // medio no llegará, así que los chats se descargan otra vez.
  if (mensajes.epoch !== msg.epoch || (!conSince.has(COL_MENSAJES) && anunciada > mensajes.seq)) {
    mensajes.epoch = msg.epoch;
    mensajes.seq = anunciada;
    mensajes.recargadosConSaludo = true;
    recargarChats();
  }
}

function recibirMensajes(msg: any) {
  if (msg.type === "resync") {
    // El búfer del VPS ya no llega: desde lo que anunció el saludo.
    if (mensajes.recargadosConSaludo) return;
    mensajes.seq = mensajes.anunciada;
    recargarChats();
    return;
  }
  if (msg.type !== "docs" || !mensajes.epoch || msg.seq <= mensajes.seq) return;
  if (msg.seq !== mensajes.seq + 1) recargarChats(); // falta alguno
  mensajes.seq = msg.seq;
  const tocados = new Set<Chat>();
  for (const [clave, datos] of msg.changes) {
    const corte = clave.indexOf("/");
    const chat = mensajes.chats.get(clave.slice(0, corte));
    if (!chat) continue;
    const id = clave.slice(corte + 1);
    if (chat.cargando) { chat.pendientes.push([mensajes.generacion, msg.seq, id, datos]); continue; }
    if (msg.seq <= chat.base) continue;
    aplicarAChat(chat, id, datos);
    tocados.add(chat);
  }
  tocados.forEach(avisarChat);
}

function soltarChat(chat: Chat) {
  if (chat.olvido) { clearTimeout(chat.olvido); chat.olvido = null; }
  if (chat.control) { const control = chat.control; chat.control = null; control.abort(); }
}

function fallarMensajes() {
  mensajes.fallido = true;
  const chats = Array.from(mensajes.chats.values());
  mensajes.chats = new Map();
  mensajes.epoch = "";
  chats.forEach((chat) => {
    soltarChat(chat);
    if (chat.subs.size) {
      let enFirestore = chatsEnFirestore.get(chat.id);
      if (!enFirestore) chatsEnFirestore.set(chat.id, enFirestore = new Set());
      chat.subs.forEach((s) => enFirestore!.add(s));
    }
    Array.from(chat.subs).forEach((s) => s.fallo());
    chat.subs.clear();
  });
  reiniciarFlujo();
  vigilarVuelta();
}

// Los chats abiertos se piden otra vez al VPS; cada suscripción cierra su
// Firestore cuando le llegan los mensajes. Si falla, vuelven a esperar.
function volverMensajes() {
  mensajes.fallido = false;
  const pendientes = Array.from(chatsEnFirestore.entries());
  chatsEnFirestore.clear();
  pendientes.forEach(([id, subs]) => {
    const chat = nuevoChat(id);
    subs.forEach((s) => chat.subs.add(s));
    mensajes.chats.set(id, chat);
    void descargarChat(chat);
  });
}

function nuevoChat(id: string): Chat {
  return { id, filas: new Map(), base: 0, cargando: false, inicio: null, pendientes: [],
    subs: new Set(), control: null, olvido: null };
}

function olvidarChat(chat: Chat) {
  if (chat.subs.size || mensajes.chats.get(chat.id) !== chat) return;
  soltarChat(chat);
  mensajes.chats.delete(chat.id);
  if (!mensajes.chats.size) { mensajes.epoch = ""; mensajes.seq = 0; }
  reiniciarFlujo();
}

/**
 * Mensajes de un chat (lo que devolvía el listener de su subcolección, sin
 * orden). Si el VPS no sirve, `alternativa` abre las páginas de Firestore.
 */
export function escucharMensajes(
  chatId: string,
  alSiguiente: (filas: Fila[]) => void,
  alternativa: () => () => void,
): () => void {
  if (!espejoConfigurado() || !auth.currentUser) return alternativa();
  let activo = true;
  let bajaAlternativa: (() => void) | null = null;
  const sub: SubMensajes = {
    emitir: (filas) => {
      if (!activo) return;
      alSiguiente(filas);
      // El VPS ha vuelto: con sus mensajes ya pintados, fuera Firestore.
      if (bajaAlternativa) { const baja = bajaAlternativa; bajaAlternativa = null; baja(); }
    },
    fallo: () => {
      if (!activo || bajaAlternativa) return;
      bajaAlternativa = alternativa();
    },
  };
  const quitar = () => {
    const enFirestore = chatsEnFirestore.get(chatId);
    if (enFirestore?.delete(sub) && !enFirestore.size) chatsEnFirestore.delete(chatId);
    const actual = mensajes.chats.get(chatId);
    if (!actual || !actual.subs.delete(sub) || actual.subs.size) return;
    actual.olvido = setTimeout(() => olvidarChat(actual), MENSAJES_RECORDADOS_MS);
  };
  if (mensajes.fallido) {
    let enFirestore = chatsEnFirestore.get(chatId);
    if (!enFirestore) chatsEnFirestore.set(chatId, enFirestore = new Set());
    enFirestore.add(sub);
    sub.fallo();
    vigilarVuelta();
    return () => {
      if (!activo) return;
      activo = false;
      bajaAlternativa?.();
      quitar();
    };
  }
  let chat = mensajes.chats.get(chatId);
  if (!chat) {
    chat = nuevoChat(chatId);
    mensajes.chats.set(chatId, chat);
    chat.subs.add(sub);
    void descargarChat(chat);
  } else {
    if (chat.olvido) { clearTimeout(chat.olvido); chat.olvido = null; }
    chat.subs.add(sub);
    const abierto = chat;
    queueMicrotask(() => {
      if (!abierto.cargando && abierto.subs.has(sub)) sub.emitir(Array.from(abierto.filas.values()));
    });
  }
  return () => {
    if (!activo) return;
    activo = false;
    bajaAlternativa?.();
    quitar();
  };
}

/**
 * Mensaje que esta pestaña está creando: se ve ya, antes de que Firestore lo
 * confirme (el listener de Firestore también lo enseñaba al instante). Lo que
 * llegue del flujo lo sustituye. Devuelve cómo retirarlo si la escritura falla.
 */
export function mensajeLocal(chatId: string, id: string, datos: Record<string, any>): () => void {
  const chat = mensajes.chats.get(chatId);
  if (!chat || chat.cargando || chat.filas.has(id)) return () => {};
  const insertada = aplicarCampos({ id }, datos);
  chat.filas.set(id, insertada);
  avisarChat(chat);
  return () => {
    if (chat.filas.get(id) !== insertada) return;
    chat.filas.delete(id);
    avisarChat(chat);
  };
}

// Al cerrar sesión (o cambiar de cuenta) no se guarda nada de la anterior.
function olvidar() {
  flujo?.abort();
  flujo = null;
  columnasFlujo = "";
  if (reintento) { clearTimeout(reintento); reintento = null; }
  avisosCambio.clear();
  seqAvisos.clear();
  avisosVuelta.clear();
  chatsEnFirestore.clear();
  pararVuelta();
  proximaVuelta = 0;
  esperaVuelta = VOLVER_CADA_MS;
  ultimaVueltaOk = 0;
  (Object.values(espejos) as Espejo[]).forEach((e) => {
    e.estado = "parado"; e.filas = new Map(); e.seq = 0; e.epoch = ""; e.anunciada = 0;
    e.pendientes = []; e.suscriptores.clear(); e.tocado();
  });
  Array.from(mensajes.chats.values()).forEach((chat) => { soltarChat(chat); chat.subs.clear(); });
  Object.assign(mensajes, { fallido: false, epoch: "", seq: 0, anunciada: 0, generacion: 0,
    recargadosConSaludo: false, chats: new Map() });
  ultimaSeñal = 0;
  sinFlujoDesde = 0;
  pararVigilancia();
  esperaReintento = 1000;
  epochFlujo = "";
  conSince = new Set();
  ultimaEpoch = "";
  usuariosEnCurso = null;
  casosSinVpsHasta = 0;
}

// Al volver a la pestaña o recuperar la red no se espera a los temporizadores
// (en una pestaña oculta Chrome los retrasa hasta un minuto).
function despertar() {
  if (oculta()) return;
  // Caído y esperando reintento, o sin señal hace rato: se reabre ya.
  if (columnasFlujo && (!flujo || reintento || Date.now() - ultimaSeñal > FLUJO_DUDOSO_MS)) {
    const viejo = flujo;
    flujo = null;
    viejo?.abort();
    if (reintento) { clearTimeout(reintento); reintento = null; }
    esperaReintento = 1000;
    const cols = columnasActivas();
    columnasFlujo = cols.join(",");
    if (cols.length) void abrirFlujo(cols);
  }
  intentarVolver();
}

let uidActual: string | null = null;
if (espejoConfigurado() && typeof window !== "undefined") {
  document.addEventListener("visibilitychange", despertar);
  window.addEventListener("online", despertar);
  window.addEventListener("focus", despertar);
}
if (espejoConfigurado()) {
  onAuthStateChanged(auth, (usuario) => {
    const uid = usuario ? usuario.uid : null;
    if (uid !== uidActual) olvidar();
    uidActual = uid;
  });
}

/** Solo para pruebas. */
export const _reiniciarParaPruebas = olvidar;
